// src/Components/Booking/BookingHistory.js
import React, { useState, useEffect } from 'react';
import {
  Card,
  Button,
  Badge,
  Form,
  Table,
  InputGroup,
  Row,
  Col
} from 'react-bootstrap';
import { format, isWithinInterval, parseISO, startOfDay, endOfDay } from 'date-fns';
import { fr } from 'date-fns/locale';
import api from '../../services/bookingApi';

const BookingHistory = ({ appointments: propAppointments, onRefresh: propOnRefresh }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [localAppointments, setLocalAppointments] = useState([]);
  const [loading, setLoading] = useState(false);

  // Date Range State
  // Default to current month or empty? User asked for filter, implies flexibility.
  // Let's default to empty (show all) or last 30 days. Empty allows seeing everything.
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Si des appointments sont passés en props, on les utilise, sinon on charge les données
  const appointments = propAppointments || localAppointments;

  useEffect(() => {
    // Si aucune prop appointments n'est passée, on charge les données
    if (!propAppointments) {
      loadAppointments();
    }
  }, [propAppointments]);

  const loadAppointments = async () => {
    setLoading(true);
    try {
      const userLocation = localStorage.getItem('userLocation');

      console.log('🔍 BookingHistory - userLocation:', userLocation);

      const filters = userLocation ? { location: userLocation } : {};

      console.log('🔍 BookingHistory - Filters envoyés:', filters);

      const data = await api.getAppointments(filters);

      console.log('📊 BookingHistory - Appointments reçus:', data.length, data);

      setLocalAppointments(data);
    } catch (err) {
      console.error("Erreur chargement historique:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    if (propOnRefresh) {
      propOnRefresh();
    } else {
      loadAppointments();
    }
  };

  const filteredAppointments = appointments.filter(app => {
    // 1. Search Term (Safe check)
    const term = searchTerm.toLowerCase().trim();
    if (term) {
      const serviceName = app.service?.name?.toLowerCase() || '';
      const clientName = app.clientName?.toLowerCase() || '';
      // Also search in services array if multiple
      const servicesNames = app.services?.map(s => s.name.toLowerCase()).join(' ') || '';

      const matchesSearch = serviceName.includes(term) || clientName.includes(term) || servicesNames.includes(term);
      if (!matchesSearch) return false;
    }

    // 2. Status
    if (statusFilter !== 'all' && app.status !== statusFilter) {
      return false;
    }

    // 3. Date Range
    if (startDate || endDate) {
      if (!app.date) return false; // If filtering by date but app has no date, skip
      try {
        // Simplify date comparison by using strings YYYY-MM-DD
        // Assuming app.date is ISO or YYYY-MM-DD
        const appDateStr = new Date(app.date).toISOString().split('T')[0];

        if (startDate && appDateStr < startDate) return false;
        if (endDate && appDateStr > endDate) return false;
      } catch (e) {
        console.error('Invalid date for appointment:', app);
        return false;
      }
    }

    return true;
  });

  const getStatusVariant = (status) => {
    const variants = {
      'confirmed': 'success',
      'pending': 'warning',
      'cancelled': 'danger',
      'completed': 'info',
      'no-show': 'secondary'
    };
    return variants[status] || 'secondary';
  };

  const formatTime = (time) => {
    if (!time) return '';
    return time.replace(':', 'h');
  };

  const formatDuration = (minutes) => {
    if (!minutes) return '';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h${mins > 0 ? `${mins}min` : ''}` : `${minutes}min`;
  };

  return (
    <Card className="shadow" style={{ marginTop: '100px' }}> {/* Added margin top for visibility */}
      <Card.Body>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <Card.Title>Historique des Réservations</Card.Title>
          <Button variant="outline-primary" onClick={handleRefresh} disabled={loading}>
            <i className="bi bi-arrow-clockwise"></i> {loading ? 'Chargement...' : 'Actualiser'}
          </Button>
        </div>

        <Row className="mb-4">
          <Col md={3}>
            <InputGroup>
              <InputGroup.Text>
                <i className="bi bi-search"></i>
              </InputGroup.Text>
              <Form.Control
                placeholder="Rechercher client/service..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </InputGroup>
          </Col>
          <Col md={2}>
            <Form.Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">Tous les statuts</option>
              <option value="confirmed">Confirmé</option>
              <option value="pending">En attente</option>
              <option value="completed">Terminé</option>
              <option value="cancelled">Annulé</option>
            </Form.Select>
          </Col>
          <Col md={3}>
            <InputGroup>
              <InputGroup.Text>Du</InputGroup.Text>
              <Form.Control
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </InputGroup>
          </Col>
          <Col md={3}>
            <InputGroup>
              <InputGroup.Text>Au</InputGroup.Text>
              <Form.Control
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </InputGroup>
          </Col>
          <Col md={1}>
            {filteredAppointments.length > 0 && (
              <Badge bg="primary" className="p-2">
                {filteredAppointments.length}
              </Badge>
            )}
          </Col>
        </Row>

        {filteredAppointments.length === 0 ? (
          <div className="text-center py-4 text-muted">
            Aucune réservation trouvée pour cette période.
          </div>
        ) : (
          <div className="table-responsive">
            <Table hover striped bordered>
              <thead className="bg-light">
                <tr>
                  <th>Date</th>
                  <th>Heure</th>
                  <th>Client</th>
                  <th>Service</th>
                  <th>Durée</th>
                  <th>Prix</th>
                  <th>Statut</th>
                </tr>
              </thead>
              <tbody>
                {filteredAppointments.map((appointment) => (
                  <tr key={appointment._id}>
                    <td>
                      {appointment.date ? (
                        format(parseISO(appointment.date), 'dd/MM/yyyy', { locale: fr })
                      ) : 'N/A'}
                    </td>
                    <td>
                      {formatTime(appointment.startTime)}
                    </td>
                    <td>
                      <div>
                        <strong>{appointment.clientName || 'N/A'}</strong>
                        {appointment.clientPhone && (
                          <div className="text-muted small">
                            {appointment.clientPhone}
                          </div>
                        )}
                      </div>
                    </td>
                    <td>
                      {/* Handle single service or multiple services (list) */}
                      {appointment.services && appointment.services.length > 0 ? (
                        <ul style={{ listStyleType: 'none', paddingLeft: 0, margin: 0 }}>
                          {appointment.services.map((s, idx) => (
                            <li key={idx}>• {s.name}</li>
                          ))}
                        </ul>
                      ) : (
                        <span>{appointment.service?.name || 'Service standard'}</span>
                      )}
                    </td>
                    <td>{formatDuration(appointment.duration)}</td>
                    <td>
                      {/* Calculate total price if multiple services, or use single price */}
                      {appointment.services && appointment.services.length > 0
                        ? appointment.services.reduce((sum, s) => sum + s.price, 0)
                        : (appointment.service?.price || 0)} DT
                    </td>

                    <td>
                      <Badge bg={getStatusVariant(appointment.status)}>
                        {appointment.status}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default BookingHistory;